// Detalle de un reporte: su ficha y las COLUMNAS que trae el CSV.
//
// Las columnas son el punto: saber si un reporte sirve sin tener que bajarlo primero.
import { Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import type { ReportDefinition, ReportFormat } from '@/api/services/retail-wizard/backoffice';
import { NoSource } from '@/shared/patterns/NoSource';
import {
    reportColumns,
    runnableFormats,
    PERIODICITY_CHIP,
    PERIODICITY_HINT,
    PERIODICITY_LABEL,
} from '../lib/reportesFormat';

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <>
            <dt className="text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </dt>
            <dd className="text-[11.5px]">{children}</dd>
        </>
    );
}

export function ReportDetailModal({
    report,
    onClose,
    onRun,
    isRunning,
    canRun,
    noRunReason,
}: {
    report: ReportDefinition | undefined;
    onClose: () => void;
    onRun: (reportId: string, format: ReportFormat) => void;
    isRunning: boolean;
    /** `retail.report.run`. Sin él la ficha se lee completa, pero no se genera nada. */
    canRun: boolean;
    noRunReason: string;
}) {
    const columns = report ? reportColumns(report) : [];

    return (
        <Modal isOpen={report !== undefined} onClose={onClose} title={report?.name ?? ''} size="lg">
            {report ? (
                <div className="flex flex-col gap-4">
                    <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5">
                        <Row label="Código">
                            {/* eslint-disable-next-line no-restricted-syntax -- código de negocio del reporte, el que se cita en los documentos */}
                            <span className="font-mono">{report.reportId}</span>
                        </Row>
                        <Row label="Periodicidad">
                            <span
                                className={cn(
                                    'rounded-full px-1.5 py-px text-[9px] font-bold',
                                    PERIODICITY_CHIP[report.periodicity],
                                )}
                            >
                                {PERIODICITY_LABEL[report.periodicity]}
                            </span>
                            <span className="ml-2 text-[10.5px] text-muted-foreground">
                                {PERIODICITY_HINT[report.periodicity]}
                            </span>
                        </Row>
                        <Row label="Descripción">{report.description}</Row>
                        <Row label="Referencia">
                            <span className="font-mono text-[10.5px]">{report.reference}</span>
                        </Row>
                        <Row label="Última corrida">
                            {report.lastRunAt
                                ? formatDateTimeMx(report.lastRunAt)
                                : 'Nunca se ha corrido'}
                        </Row>
                        <Row label="Volumen típico">
                            <NoSource reason="El endpoint del catálogo no manda volumen ni tamaño estimado. El número de filas aparece al generar el reporte." />
                        </Row>
                    </dl>

                    {/* Las columnas, no una muestra de filas: una fila de ejemplo se lee como el
                        contenido real del reporte y el operador no puede distinguirla. */}
                    <div>
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Columnas del archivo
                        </div>
                        {columns.length === 0 ? (
                            <p className="rounded-md border border-dashed border-border p-2.5 text-[10.5px] text-muted-foreground">
                                No conocemos las columnas de este reporte todavía. Se ven al
                                generarlo.
                            </p>
                        ) : (
                            <ul className="flex flex-wrap gap-1">
                                {columns.map((column) => (
                                    <li
                                        key={column}
                                        className="rounded border border-border bg-muted/40 px-1.5 py-0.5 font-mono text-[10.5px]"
                                    >
                                        {column}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
                        <p className="mr-auto text-[10.5px] leading-snug text-muted-foreground">
                            {canRun
                                ? 'El archivo se genera y se descarga en el momento. El log guarda el registro de la corrida, no el archivo.'
                                : noRunReason}
                        </p>
                        {runnableFormats(report.formats).map((format) => (
                            <button
                                key={format}
                                type="button"
                                disabled={isRunning || !canRun}
                                title={canRun ? undefined : noRunReason}
                                onClick={() => {
                                    onRun(report.reportId, format);
                                }}
                                className="inline-flex h-8 items-center rounded-md bg-primary px-3 text-[12px] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isRunning ? 'Generando…' : `Descargar ${format}`}
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}
        </Modal>
    );
}
